import React, { useState } from 'react';
import { Upload, Table, Typography, message, Spin, Tabs } from 'antd';
import { InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';
import TemplatePreview from '../../components/TemplatePreview';

const { Dragger } = Upload;
const { Title, Text } = Typography;

const FileUploadWidget = ({
    onFileUpload,
    filePreview,
    fileColumns,
    apiBaseUrl,
    authToken,
    templates = {},
    onTemplatesLoad
}) => {
    const [loading, setLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    const loadTemplates = async () => {
        if (Object.keys(templates).length > 0) return; // Already loaded

        setTemplatesLoading(true);
        try {
            const response = await fetch(`${apiBaseUrl}/templates`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            onTemplatesLoad && onTemplatesLoad(data.templates);
        } catch (error) {
            message.error('Failed to load templates configuration');
        } finally {
            setTemplatesLoading(false);
        }
    };

    const handleUpload = async (file) => {
        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${apiBaseUrl}/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setUploadedFile(file);
                onFileUpload(file, data.preview_data, data.preview_data ? Object.keys(data.preview_data[0] || {}) : []);
                message.success('File uploaded successfully!');
            } else {
                message.error('Failed to upload file');
            }
        } catch (error) {
            message.error(`Upload failed: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        name: 'file',
        multiple: false,
        accept: '.csv,.xlsx,.xls',
        beforeUpload: (file) => {
            const isValidType = file.type === 'text/csv' ||
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel';

            if (!isValidType) {
                message.error('You can only upload CSV or Excel files!');
                return false;
            }

            const maxMb = 100;
            const isLtMax = file.size / 1024 / 1024 < maxMb;
            if (!isLtMax) {
                message.error(`File must be smaller than ${maxMb}MB!`);
                return false;
            }

            handleUpload(file);
            return false; // Prevent default upload
        },
    };

    const columns = filePreview && filePreview.length > 0 ?
        Object.keys(filePreview[0]).map(key => ({
            title: key,
            dataIndex: key,
            key: key,
            ellipsis: true,
        })) : [];

    const tabItems = [
        {
            key: 'upload',
            label: (
                <span>
                    <InboxOutlined />
                    Upload File
                </span>
            ),
            children: (
                <div>
                    <Title level={4}>Upload Excel or CSV File</Title>
                    <Text type="secondary">
                        Upload your data file to begin the mapping process. Supported formats: CSV, XLS, XLSX
                    </Text>

                    <div style={{ marginTop: 24 }}>
                        <Dragger {...uploadProps} style={{ padding: '40px 20px' }}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag file to this area to upload
                            </p>
                            <p className="ant-upload-hint">
                                Support for CSV and Excel files. Maximum file size: 100MB
                            </p>
                        </Dragger>
                    </div>

                    {loading && (
                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 16 }}>
                                <Text>Processing file...</Text>
                            </div>
                        </div>
                    )}

                    {uploadedFile && filePreview && (
                        <div style={{ marginTop: 24 }}>
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>File: </Text>
                                <Text>{uploadedFile.name}</Text>
                                <br />
                                <Text strong>Columns: </Text>
                                <Text>{fileColumns.length}</Text>
                                <br />
                                <Text strong>Preview rows: </Text>
                                <Text>{filePreview.length}</Text>
                            </div>

                            <div className="file-preview">
                                <Table
                                    dataSource={filePreview}
                                    columns={columns}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: true }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'templates',
            label: (
                <span>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    View Data Templates
                </span>
            ),
            children: (
                <div>
                    {templatesLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 16 }}>
                                <Text>Loading templates...</Text>
                            </div>
                        </div>
                    ) : (
                        <TemplatePreview templates={templates} />
                    )}
                </div>
            )
        }
    ];

    return (
        <div>
            <Tabs
                defaultActiveKey="upload"
                items={tabItems}
                onChange={(key) => {
                    if (key === 'templates') {
                        loadTemplates();
                    }
                }}
            />
        </div>
    );
};

export default FileUploadWidget;
